import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Hero from "@/components/ui/Hero";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Star } from "lucide-react";

const SERVICE_OPTIONS = [
    "Pool cage rescreen",
    "Lanai repair",
    "Pool cage repair",
    "Screen door / sliding door",
    "Storm-damage repair",
    "Other",
];

export default function LeaveAReview() {
    useDocumentTitle(
        "Leave a Review · A1 Screening · Naples, FL",
        "Share your experience with A1 Screening. Reviews are moderated before they appear on the website.",
    );

    const [form, setForm] = useState({
        customer_name: "",
        rating: 0,
        review_text: "",
        service_type: "",
        city_or_area: "",
        email_or_phone: "",
        permission_confirmed: false,
    });
    const [hoverRating, setHoverRating] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const startMs = useRef(Date.now());
    const [honeypot, setHoneypot] = useState("");

    useEffect(() => {
        startMs.current = Date.now();
    }, []);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (form.customer_name.trim().length < 2) {
            setError("Please enter your name.");
            return;
        }
        if (form.rating < 1 || form.rating > 5) {
            setError("Please select a star rating.");
            return;
        }
        if (form.review_text.trim().length < 10) {
            setError("Please share a few sentences about your experience.");
            return;
        }
        if (!form.permission_confirmed) {
            setError("Please confirm permission to display your review.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/reviews/submit", {
                ...form,
                website_url: honeypot,
                start_ms: startMs.current,
            });
            setSuccess(res.data.message);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Sorry — something went wrong. Please try again or call 239-434-7306.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Hero
                eyebrow="Customer Reviews"
                title="Leave a Review"
                subtitle="Share your experience with A1 Screening. Reviews are reviewed by our team before appearing on the website."
            />
            <section className="a1-section">
                <div className="a1-container max-w-2xl">
                    {success ? (
                        <div className="a1-card" data-testid="review-success">
                            <h2 className="text-2xl mb-2">Thank you.</h2>
                            <p style={{ color: "var(--color-text-muted)" }}>{success}</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5" data-testid="review-form" noValidate>
                            <div>
                                <label className="a1-label" htmlFor="rv-name">Your name</label>
                                <input
                                    id="rv-name"
                                    type="text"
                                    className="a1-input"
                                    value={form.customer_name}
                                    onChange={(e) => update("customer_name", e.target.value)}
                                    placeholder="First and last name"
                                    autoComplete="name"
                                    data-testid="review-input-name"
                                    required
                                />
                            </div>

                            <div>
                                <label className="a1-label">Rating</label>
                                <div
                                    className="flex items-center gap-2"
                                    role="radiogroup"
                                    aria-label="Star rating"
                                    data-testid="review-rating"
                                >
                                    {[1, 2, 3, 4, 5].map((n) => {
                                        const filled = n <= (hoverRating || form.rating);
                                        return (
                                            <button
                                                key={n}
                                                type="button"
                                                aria-label={`${n} star${n === 1 ? "" : "s"}`}
                                                aria-pressed={form.rating === n}
                                                role="radio"
                                                aria-checked={form.rating === n}
                                                onMouseEnter={() => setHoverRating(n)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                onClick={() => update("rating", n)}
                                                data-testid={`review-star-${n}`}
                                                className="p-1 transition-transform hover:scale-110"
                                                style={{ background: "transparent", border: "none", cursor: "pointer" }}
                                            >
                                                <Star
                                                    size={36}
                                                    fill={filled ? "var(--color-cta)" : "transparent"}
                                                    color="var(--color-cta)"
                                                    strokeWidth={1.5}
                                                />
                                            </button>
                                        );
                                    })}
                                    <span className="ml-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
                                        {form.rating ? `${form.rating} / 5` : "Tap to rate"}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="a1-label" htmlFor="rv-text">Your review</label>
                                <textarea
                                    id="rv-text"
                                    className="a1-input"
                                    rows={5}
                                    value={form.review_text}
                                    onChange={(e) => update("review_text", e.target.value)}
                                    placeholder="What did A1 Screening do for you, how did it go, and what would you tell a neighbor?"
                                    data-testid="review-input-text"
                                    required
                                    minLength={10}
                                    maxLength={2000}
                                />
                                <p className="a1-helper">
                                    {form.review_text.length} / 2000 characters
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="a1-label" htmlFor="rv-service">Service (optional)</label>
                                    <select
                                        id="rv-service"
                                        className="a1-input"
                                        value={form.service_type}
                                        onChange={(e) => update("service_type", e.target.value)}
                                        data-testid="review-input-service"
                                    >
                                        <option value="">— Choose —</option>
                                        {SERVICE_OPTIONS.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="a1-label" htmlFor="rv-city">Neighborhood / city (optional)</label>
                                    <input
                                        id="rv-city"
                                        type="text"
                                        className="a1-input"
                                        value={form.city_or_area}
                                        onChange={(e) => update("city_or_area", e.target.value)}
                                        placeholder="e.g. Naples, Pelican Bay, Fort Myers"
                                        data-testid="review-input-city"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="a1-label" htmlFor="rv-contact">
                                    Email or phone (optional — for verification only, never shown publicly)
                                </label>
                                <input
                                    id="rv-contact"
                                    type="text"
                                    className="a1-input"
                                    value={form.email_or_phone}
                                    onChange={(e) => update("email_or_phone", e.target.value)}
                                    placeholder="email or phone"
                                    data-testid="review-input-contact"
                                />
                                <p className="a1-helper">
                                    We may use this to confirm your review is real before it appears on the website.
                                    It will <strong>never</strong> be displayed publicly.
                                </p>
                            </div>

                            <label className="flex items-start gap-2 text-sm" data-testid="review-consent-label">
                                <input
                                    type="checkbox"
                                    checked={form.permission_confirmed}
                                    onChange={(e) => update("permission_confirmed", e.target.checked)}
                                    data-testid="review-consent-checkbox"
                                    required
                                    aria-required="true"
                                    style={{ accentColor: "var(--color-accent)", marginTop: "0.2rem" }}
                                />
                                <span style={{ color: "var(--color-text-muted)" }}>
                                    I confirm this is a real review based on my experience with A1 Screening, and I
                                    give A1 Screening permission to display it on the website.
                                </span>
                            </label>

                            {/* Honeypot */}
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                                name="website_url"
                            />

                            {error ? (
                                <div className="a1-card text-sm" role="alert" data-testid="review-error"
                                     style={{ background: "rgba(220,38,38,0.08)", borderColor: "var(--color-danger)", color: "var(--color-danger)" }}>
                                    {error}
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                className="a1-btn-primary"
                                disabled={submitting}
                                data-testid="review-submit-btn"
                            >
                                {submitting ? "Sending…" : "Submit review"}
                            </button>
                            <p className="a1-helper">
                                After you submit, A1 Screening will review your message before it appears on the website.
                                Questions? Call <a href="tel:+12394347306" style={{ color: "var(--color-accent)" }}>239-434-7306</a>.
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </>
    );
}
